import {
    cloneFillAndSerializeQaData,
    QA_SAVE_WORKER_MESSAGE_TYPES,
} from "./qaSaveSerializationCore";

export const QA_SAVE_SERIALIZATION_TIMEOUT_MS = 30000;
export const QA_SAVE_CELLS_PER_BATCH = 1000;
export const QA_SAVE_SERIALIZATION_ERROR_CODE = "QA_SAVE_SERIALIZATION_FAILED";
export const QA_SAVE_SERIALIZATION_TIMEOUT_CODE = "QA_SAVE_SERIALIZATION_TIMEOUT";

function createQaSaveWorker() {
    return new Worker(new URL("./qaSaveSerialization.worker.js", import.meta.url), {
        type: "module",
    });
}

function serializeSynchronously(qaData, fallbackReason) {
    return {
        json: cloneFillAndSerializeQaData(qaData),
        usedWorker: false,
        fallbackReason,
    };
}

function yieldToMainThread() {
    return new Promise((resolve) => setTimeout(resolve, 0));
}

function createSerializationError(reason, details = {}) {
    const timedOut = reason === "worker-timeout";
    const workerMessage = details.workerMessage || details.workerErrorMessage || "";
    const failureDetails = workerMessage
        ? " (" + reason + ": " + workerMessage + ")"
        : "";
    const error = new Error(
        timedOut
            ? "QA save preparation timed out. Please retry."
            : "QA save preparation failed" + failureDetails + ". Please retry.",
    );
    error.name = "QaSaveSerializationError";
    error.code = timedOut
        ? QA_SAVE_SERIALIZATION_TIMEOUT_CODE
        : QA_SAVE_SERIALIZATION_ERROR_CODE;
    error.reason = reason;
    error.retryable = true;
    Object.assign(error, details);
    return error;
}

async function postQaDataInBatches({
    worker,
    requestId,
    qaData,
    shouldStop,
    yieldControl,
}) {
    for (let pageIndex = 0; pageIndex < qaData.length; pageIndex += 1) {
        if (shouldStop()) return;
        const page = qaData[pageIndex];
        if (!Array.isArray(page)) {
            throw new TypeError("Invalid QA page data");
        }

        worker.postMessage({
            type: QA_SAVE_WORKER_MESSAGE_TYPES.PAGE,
            requestId,
            pageIndex,
            rowCount: page.length,
        });

        for (let rowIndex = 0; rowIndex < page.length; rowIndex += 1) {
            if (shouldStop()) return;
            const row = page[rowIndex];
            if (!Array.isArray(row)) {
                throw new TypeError("Invalid QA row data");
            }

            worker.postMessage({
                type: QA_SAVE_WORKER_MESSAGE_TYPES.ROW,
                requestId,
                pageIndex,
                rowIndex,
                cellCount: row.length,
            });

            for (
                let cellStart = 0;
                cellStart < row.length;
                cellStart += QA_SAVE_CELLS_PER_BATCH
            ) {
                if (shouldStop()) return;
                worker.postMessage({
                    type: QA_SAVE_WORKER_MESSAGE_TYPES.CELLS,
                    requestId,
                    pageIndex,
                    rowIndex,
                    cellStart,
                    cells: row.slice(cellStart, cellStart + QA_SAVE_CELLS_PER_BATCH),
                });
                await yieldControl();
            }

            if (row.length === 0) {
                await yieldControl();
            }
        }
    }

    if (shouldStop()) return;
    worker.postMessage({
        type: QA_SAVE_WORKER_MESSAGE_TYPES.SERIALIZE,
        requestId,
    });
}

export function serializeQaDataForSave(qaData, options = {}) {
    const workerFactory = options.workerFactory || createQaSaveWorker;
    const yieldControl = typeof options.yieldControl === "function"
        ? options.yieldControl
        : yieldToMainThread;
    const timeoutMs = Number.isFinite(options.timeoutMs)
        ? Math.max(1, options.timeoutMs)
        : QA_SAVE_SERIALIZATION_TIMEOUT_MS;

    if (typeof Worker === "undefined" && !options.workerFactory) {
        return Promise.resolve(serializeSynchronously(qaData, "worker-unavailable"));
    }

    let worker;
    try {
        worker = workerFactory();
    } catch {
        return Promise.resolve(serializeSynchronously(qaData, "worker-start-failed"));
    }
    if (!worker || typeof worker.postMessage !== "function") {
        worker?.terminate?.();
        return Promise.resolve(serializeSynchronously(qaData, "worker-start-failed"));
    }

    return new Promise((resolve, reject) => {
        const requestId = `qa-save-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        let settled = false;
        let timeoutId;

        const cleanup = () => {
            worker.terminate?.();
        };

        const finishWithFallback = (fallbackReason) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            cleanup();
            try {
                resolve(serializeSynchronously(qaData, fallbackReason));
            } catch (error) {
                reject(error);
            }
        };

        const finishWithError = (reason, details) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            cleanup();
            reject(createSerializationError(reason, details));
        };

        worker.onmessage = (event) => {
            if (settled || event?.data?.requestId !== requestId) return;
            if (event?.data?.errorCode) {
                finishWithError("worker-response-error", {
                    workerErrorCode: event.data.errorCode,
                    workerErrorMessage: event.data.errorMessage,
                });
                return;
            }
            if (typeof event?.data?.json !== "string") {
                finishWithError("worker-response-invalid");
                return;
            }
            settled = true;
            clearTimeout(timeoutId);
            cleanup();
            resolve({
                json: event.data.json,
                usedWorker: true,
            });
        };
        worker.onerror = (event) => finishWithError("worker-error", {
            workerMessage: event?.message,
            workerFilename: event?.filename,
            workerLine: event?.lineno,
            workerColumn: event?.colno,
        });
        worker.onmessageerror = () => finishWithError("worker-message-error");

        try {
            worker.postMessage({
                type: QA_SAVE_WORKER_MESSAGE_TYPES.START,
                requestId,
                pageCount: qaData.length,
            });
        } catch {
            finishWithFallback("worker-post-failed");
            return;
        }

        void postQaDataInBatches({
            worker,
            requestId,
            qaData,
            shouldStop: () => settled,
            yieldControl,
        }).then(() => {
            if (settled) return;
            timeoutId = setTimeout(
                () => finishWithError("worker-timeout"),
                timeoutMs,
            );
        }).catch((error) => finishWithError("worker-post-failed", {
            workerMessage: error?.message,
        }));
    });
}
